import { useState, useEffect } from 'react';
import { storeTasksApi } from '../services/api';
import type { PendingTask, PendingTasksResponse } from '../services/api';
import type { PausedTask } from '../hooks/useVisitTask';
import { startableSets, startableCalls } from '../utils/siteTasks';

interface TaskSelectorProps {
  storeId: number;
  storeName: string;
  date?: string;  // YYYY-MM-DD format, defaults to today
  projectId?: number;  // Project ID for store disambiguation (legacystoreid is not unique)
  onSelectTask: (task: PendingTask) => void;
  onNoMoreTasks: () => void;  // Called when user chooses to proceed without more tasks
  /**
   * Called when the rep clicks the resume button for a paused task. Receives
   * the `${task_type}:${task_id}:${project_id}` key so the hook can find the
   * matching paused record when multiple tasks are paused (#1897).
   */
  onResumePausedTask?: (taskKey: string) => void;
  excludeTaskIds?: number[];  // Tasks already completed in this visit
  /**
   * All currently-paused tasks in this visit. Rendered as N independent
   * resume chips so the rep can resume any of them (#1897 — was a single
   * slot before, which silently lost work when the rep paused a second task).
   */
  pausedTasks?: PausedTask[];
  isLoading?: boolean;
}

export default function TaskSelector({
  storeId,
  storeName: _storeName,  // Available for future use
  date,
  projectId,
  onSelectTask,
  onNoMoreTasks,
  onResumePausedTask,
  excludeTaskIds = [],
  pausedTasks = [],
  isLoading: externalLoading = false,
}: TaskSelectorProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingTasks, setPendingTasks] = useState<PendingTasksResponse | null>(null);
  // Covering for a colleague is opt-in (#3454): the rep's own work leads, and
  // widening is the deliberate "I'm here anyway" step.
  const [includeAllReps, setIncludeAllReps] = useState(false);

  useEffect(() => {
    async function fetchTasks() {
      setIsLoading(true);
      setError(null);
      try {
        // Pass projectId for store disambiguation (legacystoreid is not unique)
        const response = await storeTasksApi.getPendingTasks(storeId, date, projectId, includeAllReps);
        setPendingTasks(response);
      } catch (err) {
        console.error('Failed to fetch pending tasks:', err);
        setError(err instanceof Error ? err.message : 'Failed to load tasks');
      } finally {
        setIsLoading(false);
      }
    }
    fetchTasks();
  }, [storeId, date, projectId, includeAllReps]);

  // Filter out already completed tasks + depleted calls — the ONE shared
  // inclusion rule (#2125), so the picker always agrees with the banner
  // count and the Other-tasks panel. A 0-of-0 route is not startable and
  // rendering it ("0 of 0 visits remaining") only confused reps (diag #52).
  const availableSets = pendingTasks ? startableSets(pendingTasks.sets, excludeTaskIds) : [];
  const availableCalls = pendingTasks ? startableCalls(pendingTasks.calls, excludeTaskIds) : [];

  const totalTasks = availableSets.length + availableCalls.length;

  if (isLoading || externalLoading) {
    return (
      <div className="rounded-xl bg-white p-6 shadow-card">
        <div className="flex items-center justify-center gap-3 py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
          <span className="text-slate-600">Loading available tasks...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 p-6">
        <div className="flex items-start gap-3">
          <svg className="h-6 w-6 flex-shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <h4 className="font-semibold text-red-800">Failed to load tasks</h4>
            <p className="mt-1 text-sm text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Only show "no tasks" if there are no tasks AND no paused tasks to resume
  if (totalTasks === 0 && pausedTasks.length === 0) {
    return (
      <div className="rounded-xl bg-slate-50 p-6">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-200">
            <svg className="h-6 w-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h4 className="font-semibold text-slate-700">No tasks available</h4>
          <p className="mt-1 text-sm text-slate-500">There are no pending tasks at this store.</p>
          <button
            onClick={onNoMoreTasks}
            className="mt-4 rounded-lg bg-primary-600 px-6 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  const hasPaused = pausedTasks.length > 0;
  return (
    <div className="rounded-xl bg-white p-4 shadow-card">
      <h3 className="mb-4 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
        {hasPaused ? 'Resume or Select a Task' : `Select a Task (${totalTasks} available)`}
      </h3>

      {/* Resume Paused Tasks Section — one chip per paused task (#1897) */}
      {hasPaused && onResumePausedTask && (
        <div className="mb-4">
          <h4 className="mb-2 flex items-center gap-2 text-sm font-medium text-amber-700">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {pausedTasks.length > 1 ? `Paused Tasks (${pausedTasks.length})` : 'Paused Task'}
          </h4>
          <div className="space-y-2">
            {pausedTasks.map((paused) => {
              const key = `${paused.task.task_type}:${paused.task.task_id}:${paused.task.project_id}`;
              return (
                <button
                  key={key}
                  onClick={() => onResumePausedTask(key)}
                  className="flex w-full items-center gap-3 rounded-lg border-2 border-amber-400 bg-amber-50 p-3 text-left transition-all hover:border-amber-500 hover:bg-amber-100 active:scale-[0.99]"
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                    <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">
                      {paused.task.project_description}
                    </p>
                    <p className="text-xs text-amber-700">
                      {paused.accumulatedMinutes} min already tracked - tap to resume
                    </p>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    paused.task.task_type === 'set'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {paused.task.task_type === 'set' ? 'Set' : 'Call'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Sets Section */}
      {availableSets.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
            <svg className="h-4 w-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
            Scheduled Sets
          </h4>
          <div className="space-y-2">
            {availableSets.map((task) => (
              <button
                key={`set-${task.task_id}`}
                onClick={() => onSelectTask(task)}
                className="flex w-full items-center gap-3 rounded-lg border border-purple-200 bg-purple-50 p-3 text-left transition-all hover:border-purple-300 hover:bg-purple-100 active:scale-[0.99]"
              >
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-purple-100">
                  <svg className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 truncate">
                    {task.project_description}
                  </p>
                  <p className="text-xs text-slate-500">
                    {task.client_name && <span className="mr-2">{task.client_name}</span>}
                    {task.set_hours && <span>{task.set_hours}h estimated</span>}
                  </p>
                  {task.window_start && task.window_end && (
                    <p className="text-xs text-purple-600">
                      Window: {new Date(task.window_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(task.window_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  )}
                </div>
                <svg className="h-5 w-5 flex-shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Calls Section */}
      {availableCalls.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
            <svg className="h-4 w-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Regular Calls
          </h4>
          <div className="space-y-2">
            {availableCalls.map((task) => (
              <button
                key={`call-${task.task_id}`}
                onClick={() => onSelectTask(task)}
                className="flex w-full items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-left transition-all hover:border-blue-300 hover:bg-blue-100 active:scale-[0.99]"
              >
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                  <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 truncate">
                    {task.project_description}
                  </p>
                  <p className="text-xs text-slate-500">
                    {task.client_name && <span className="mr-2">{task.client_name}</span>}
                    {/* ternary (#2125): `0 && ...` renders a literal "0" in
                        JSX — the "BBD 0" oddity in diag #52's screenshot. */}
                    {task.hours_per_visit ? <span>{task.hours_per_visit}h per visit</span> : null}
                  </p>
                  {task.visits_remaining != null && task.frequency != null ? (
                    <p className="text-xs text-blue-600">
                      {task.visits_remaining} of {task.frequency} visits remaining
                    </p>
                  ) : null}
                  {/* Covering for someone. Says whose call it is so the rep
                      knows this is a favour, not their own schedule — doing it
                      credits the store and never moves the assignment (#3454). */}
                  {task.assigned_to ? (
                    <p className="text-xs font-medium text-amber-700">Assigned to {task.assigned_to}</p>
                  ) : null}
                </div>
                <svg className="h-5 w-5 flex-shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Cover for a colleague. Opt-in rather than always-on: the rep's own
          work leads, and this is the deliberate "I'm here anyway" step. */}
      {!includeAllReps && (
        <button
          onClick={() => setIncludeAllReps(true)}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-amber-300 bg-amber-50 py-3 text-sm font-medium text-amber-800 transition-colors hover:bg-amber-100"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Show calls assigned to other reps
        </button>
      )}
      {includeAllReps && (
        <p className="mt-2 text-center text-xs text-slate-500">
          Showing other reps' calls too. Anything you complete is recorded as your work.
        </p>
      )}

      {/* No More Tasks Button */}
      <button
        onClick={onNoMoreTasks}
        className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 py-3 text-sm font-medium text-slate-600 transition-colors hover:border-slate-400 hover:bg-slate-50"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
        Skip - No more tasks to complete
      </button>
    </div>
  );
}
